import dayjs from 'dayjs';
import { ageAt, ageLabel, gestationalLabel, prematurityDays, toMonths } from '../motorMilestones/motorMilestones';

/**
 * Escala Motora Infantil de Alberta (AIMS). A escala é licenciada (Piper &
 * Darrah, 1994; Elsevier): o app nunca reproduz os itens nem as curvas de
 * percentil. O profissional aplica com o material oficial e registra o escore
 * de cada postura e, se quiser, o percentil lido no gráfico oficial. Daqui
 * saem só o total, a faixa pelo percentil e a idade (corrigida no prematuro).
 */

/** Posturas na ordem da folha, com o escore máximo de cada (o mesmo de AimsAssessments::SUBSCALES). */
export const SUBSCALES = Object.freeze([
  { key: 'proneScore', label: 'Prono', max: 21 },
  { key: 'supineScore', label: 'Supino', max: 9 },
  { key: 'sittingScore', label: 'Sentado', max: 12 },
  { key: 'standingScore', label: 'Em pé', max: 16 },
]);

export const TOTAL_MAX = SUBSCALES.reduce((sum, item) => sum + item.max, 0);

// Faixa etária para a qual a escala foi normatizada (do nascimento à marcha independente)
export const AGE_MAX_MONTHS = 18;

export const AIMS_ATTRIBUTION =
  'Escala Motora Infantil de Alberta (AIMS), Piper & Darrah (1994). Escores registrados a partir da aplicação com o material oficial; os itens e as curvas de percentil não são reproduzidos.';

/**
 * Faixa pelo percentil (Piper & Darrah, 1994): até o 5º, atraso; até o 25º,
 * suspeito (acompanhar); acima, esperado. Sem percentil, null.
 * tone: alert (laranja), info (azul), ok (verde).
 */
export function percentileBand(percentile) {
  if (percentile == null || percentile === '') return null;
  const value = Number(percentile);
  if (!Number.isFinite(value)) return null;
  if (value <= 5) return { tone: 'alert', label: 'Atraso motor' };
  if (value <= 25) return { tone: 'info', label: 'Suspeito: acompanhar' };
  return { tone: 'ok', label: 'Desenvolvimento esperado' };
}

export const BAND_NOTE = 'Faixas pelo percentil (Piper & Darrah, 1994): até o 5º, atraso; do 6º ao 25º, suspeito; acima do 25º, esperado.';

/** Soma dos escores das quatro posturas; null se faltar alguma. */
export function totalScore(record) {
  if (!record) return null;
  let sum = 0;
  for (const item of SUBSCALES) {
    const value = record[item.key];
    if (value == null || value === '' || !Number.isFinite(Number(value))) return null;
    sum += Number(value);
  }
  return sum;
}

/** Idade na avaliação ({ chronological, corrected, days }) ou null sem nascimento válido. */
export const assessmentAge = (record, birthday, birthInfo) => (record ? ageAt(birthday, record.assessedAt, birthInfo) : null);

export const formatAge = (age) => ageLabel(age);

/** true quando a idade (corrigida, se houver) passa da faixa da escala. */
export const isOutOfRange = (age) => !!age && toMonths(age.days) > AGE_MAX_MONTHS;

/** "Idade corrigida: nasceu com 34 semanas" para prematuro; null nos demais. */
export function gestationalNote(birthInfo) {
  if (prematurityDays(birthInfo) <= 0) return null;
  return `Idade corrigida: nasceu com ${gestationalLabel(birthInfo)}`;
}

export const formatPercentile = (value) => (value == null || value === '' ? '-' : `P${value}`);

// ─── Histórico ──────────────────────────────────────────────────────────────

export const sortByDateDesc = (list) => [...(Array.isArray(list) ? list : [])]
  .sort((a, b) => dayjs(b.assessedAt).valueOf() - dayjs(a.assessedAt).valueOf() || Number(b.id) - Number(a.id));

export function findOwnRecord(list, appointmentId) {
  if (appointmentId == null) return null;
  return (Array.isArray(list) ? list : []).find((item) => String(item.appointmentId) === String(appointmentId)) || null;
}

export const formatDate = (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '');

/**
 * Linhas da tabela de histórico, mais recente primeiro: o registro, a idade,
 * o total e a faixa.
 */
export function buildRows(list, birthday, birthInfo) {
  return sortByDateDesc(list).map((record) => ({
    record,
    age: assessmentAge(record, birthday, birthInfo),
    total: totalScore(record),
    band: percentileBand(record.percentile),
  }));
}
